import type { BooleanOptional, IStringifyOptions } from 'qs';
import type { CommonGlobalFilter, QuickFilter, SortInfo, TabInfo } from '@weberus/shared-types';
import { atom } from 'jotai';
import { produce } from 'immer';
import { stringify } from 'qs';
import { globalFilterTypeCheckers } from '@/app/_components/feature/global-filter-modal/global-filter-type-checkers';
import { hasItems } from '../utils/has-items';

export interface GlobalFilter {
  key: string;
  code?: string;
  value?: {
    from: number;
    to: number;
  };
}

/** 세션 스토리지에서 사용할 키 */
export const sessionKeyAtom = atom<string>('');

export const searchTypeAtom = atom<string>();
export const searchTypeKeyAtom = atom<'searchType' | 'shortcut' | 'category'>('searchType');
export const searchFilterSortAtom = atom<string>();

export const globalFilterParamsAtom = atom<GlobalFilter[]>();
export const searchFilterKeywordAtom = atom<string>();
export const sortInfoAtom = atom<SortInfo | null>();
export const originalGlobalFilterAtom = atom<CommonGlobalFilter | null>();
export const tabInfoAtom = atom<TabInfo['items']>();
/** 글로벌 필터 모달의 초기 선택 영역의 키 */
export const globalFilterSelectedAreaKeyAtom = atom<string>();

export const isTabScrollUpdatingAtom = atom(false);

// [getter] tempGlobalFilterParams 계산된 상태
export const tempGlobalFilterParamsAtom = atom((get) => {
  const globalFilterItems = get(globalFilterAtom);
  if (!globalFilterItems) return undefined;

  const filterArray: GlobalFilter[] = [];
  const switchFilterArray: GlobalFilter[] = [];

  globalFilterItems.items.forEach((item) => {
    if (globalFilterTypeCheckers.isSwitchType(item)) {
      const { selected, key } = item.items?.[0] || {};
      if (selected) {
        switchFilterArray.push({ key });
      }
    } else if (globalFilterTypeCheckers.isChipType(item) || globalFilterTypeCheckers.isCheckboxListType(item)) {
      item.items
        .filter(({ selected }) => selected)
        .forEach((subItem) => filterArray.push({ key: subItem.key, code: subItem.code }));
    } else if (globalFilterTypeCheckers.isAccordionType(item)) {
      item.items.forEach((subItem) => {
        subItem.items
          .filter(({ selected }) => selected)
          .forEach((chip) => filterArray.push({ key: chip.key, code: chip.code }));
      });
    } else if (globalFilterTypeCheckers.isHistogramNumberRangeType(item)) {
      const { selected, value, key } = item.items?.[0] || {};
      if (selected && value) {
        filterArray.push({ key, value: { from: value.from, to: value.to } });
      }
    } else if (globalFilterTypeCheckers.isHistogramNumberRangeWithoutUpperLimitType(item)) {
      const { selected, value, key } = item.items?.[0] || {};
      if (selected && value) {
        filterArray.push({ key, value: { from: value.from, to: value.to } });
      }
    }
  });

  return [...filterArray, ...switchFilterArray];
});

// quickFilter 상태를 관리 -- BEGIN
export type QuickFilterWithGlobalFlag = {
  globalFilter: CommonGlobalFilter | undefined | null;
  quickFilter: QuickFilter | undefined | null;
  hasSelectedFilter: boolean;
  includeFilterBtn: boolean;
} & {
  items: (QuickFilter['items'][0] & Partial<{ isGlobalFilter: boolean }>)[];
};
const baseQuickFilterAtom = atom<QuickFilterWithGlobalFlag | undefined>(undefined);
export const quickFilterAtom = atom(
  (get) => get(baseQuickFilterAtom),
  (
    _,
    set,
    data: {
      globalFilter: CommonGlobalFilter | undefined | null;
      quickFilter: QuickFilter | undefined | null;
      hasSelectedFilter: boolean;
      includeFilterBtn: boolean;
    },
  ) => {
    const globalFilterItem =
      data.includeFilterBtn && hasItems(data.globalFilter?.items)
        ? [
            {
              key: '',
              logMeta: {} as Record<string, unknown>,
              type: 'OPEN_GLOBAL_FILTER' as const,
              selected: !!data.hasSelectedFilter,
              title: '필터',
              isGlobalFilter: true,
              disabled: !data.includeFilterBtn,
              visible: data.includeFilterBtn,
            },
          ]
        : [];
    const quickFilterItems: QuickFilterWithGlobalFlag['items'] = data.quickFilter?.items || [];
    const res: QuickFilterWithGlobalFlag | undefined = hasItems([...globalFilterItem, ...quickFilterItems])
      ? {
          ...data,
          items: [...globalFilterItem, ...quickFilterItems],
        }
      : undefined;

    set(baseQuickFilterAtom, res);
  },
);
// quickFilter 상태를 관리 -- END

/** [getter] isResetButtonEnabledAtom 계산된 상태 */
export const isResetButtonEnabledAtom = atom((get) => {
  const globalFilter = get(globalFilterAtom);

  if (!globalFilter) return false;

  return globalFilter.items.some((item) => {
    if (globalFilterTypeCheckers.isAccordionType(item)) {
      return item.items.some((subItem) => subItem.items.some((chip) => chip.selected));
    }
    return item.items.some((subItem) => subItem.selected);
  });
});

// globalFilterItems 상태를 관리 -- BEGIN
const baseGlobalFilterAtom = atom<CommonGlobalFilter | undefined | null>(undefined);
export const globalFilterAtom = atom(
  (get) => get(baseGlobalFilterAtom),
  (_, set, data: CommonGlobalFilter | undefined | null) => {
    set(baseGlobalFilterAtom, data);
  },
);
// globalFilterItems 상태를 관리 -- END

// [setter] resetGlobalFilterItems
export const resetGlobalFilterItemsAtom = atom(null, (get, set) => {
  const globalFilterItems = get(globalFilterAtom);

  if (!globalFilterItems?.items) return;

  const newState = produce(globalFilterItems, (draft) => {
    draft.items.forEach((item) => {
      if (globalFilterTypeCheckers.isSwitchType(item)) {
        item.items[0].selected = false;
      } else if (globalFilterTypeCheckers.isChipType(item) || globalFilterTypeCheckers.isCheckboxListType(item)) {
        item.items.forEach((subItem) => {
          subItem.selected = false;
        });
      } else if (globalFilterTypeCheckers.isAccordionType(item)) {
        item.items.forEach((subItem) => {
          subItem.items.forEach((chip) => {
            chip.selected = false;
          });
        });
      } else if (globalFilterTypeCheckers.isHistogramNumberRangeType(item)) {
        item.items[0].selected = false;
        item.items[0].value = { from: item.range.min, to: item.range.max };
      } else if (globalFilterTypeCheckers.isHistogramNumberRangeWithoutUpperLimitType(item)) {
        item.items[0].selected = false;
        item.items[0].value = { from: item.range.min, to: item.range.max };
      }
    });
  });

  set(globalFilterAtom, newState);
});

/** [getter] 사용자가 선택한 글로벌 필터의 값을 쿼리스트링으로 변환 */
export const globalFilterQueryStrAtom = atom((get) => {
  const globalFilterItems = get(globalFilterAtom);
  if (!globalFilterItems) {
    return {
      filterQuery: undefined,
      switchFilterQuery: undefined,
      rangeFilterQuery: undefined,
    };
  }

  // filter 는 ICodeTypeDetailFilterItem 타입과 대응
  const filterObj: Record<string, string[]> = {};
  // switchFilter 는 IBoolTypeDetailFilterItem 타입과 대응
  const switchFilterObj: Record<string, boolean> = {};
  // rangeFilter 는 INumberRangeTypeDetailFilterItem 타입과 대응
  const rangeFilterObj: Record<string, [number, number]> = {};

  globalFilterItems.items.forEach((item) => {
    if (globalFilterTypeCheckers.isSwitchType(item)) {
      const { selected, key } = item.items?.[0] || {};
      if (selected) {
        switchFilterObj[key] = true;
      }
    } else if (globalFilterTypeCheckers.isChipType(item) || globalFilterTypeCheckers.isCheckboxListType(item)) {
      item.items
        .filter(({ selected }) => selected)
        .forEach((subItem) => {
          if (!filterObj[subItem.key]) {
            filterObj[subItem.key] = [];
          }
          filterObj[subItem.key].push(subItem.code);
        });
    } else if (globalFilterTypeCheckers.isAccordionType(item)) {
      item.items.forEach((subItem) => {
        subItem.items
          .filter(({ selected }) => selected)
          .forEach((chip) => {
            if (!filterObj[chip.key]) {
              filterObj[chip.key] = [];
            }
            filterObj[chip.key].push(chip.code);
          });
      });
    } else if (globalFilterTypeCheckers.isHistogramNumberRangeType(item)) {
      const { selected, key } = item.items?.[0] || {};
      if (selected) {
        const value = item.items?.[0].value;
        if (value) {
          rangeFilterObj[key] = [value.from, value.to];
        }
      }
    } else if (globalFilterTypeCheckers.isHistogramNumberRangeWithoutUpperLimitType(item)) {
      const { selected, key } = item.items?.[0] || {};
      if (selected) {
        const value = item.items?.[0].value;
        if (value) {
          rangeFilterObj[key] = [value.from, value.to];
        }
      }
    }
  });

  const stringifyOptions: IStringifyOptions<BooleanOptional> = {
    arrayFormat: 'comma',
    skipNulls: true,
    filter: (_, val) => (val ? val : undefined), // 빈 문자열('') 제거
  };

  const filterQuery = stringify(filterObj, stringifyOptions) || undefined;
  const switchFilterQuery = stringify(switchFilterObj, stringifyOptions) || undefined;
  const rangeFilterQuery = stringify(rangeFilterObj, stringifyOptions) || undefined;

  return {
    filterQuery,
    switchFilterQuery,
    rangeFilterQuery,
    filterObj,
    switchFilterObj,
    rangeFilterObj,
  };
});
